import { LINK_SOURCE } from 'components_v2/common'
import { camelToSnake } from 'utils'

window.dataLayer = window.dataLayer || []

const set_user_properties = (properties) => {
  const camelizedData = camelToSnake(properties)
  const userId = camelizedData?.user_id
  delete camelizedData.user_id
  return {
    ...camelizedData,
    userId,
    user_alo_tier: camelizedData?.user_alo_tier || 'na'
  }
}

export const premium_checkout_clicked_event = (
  type,
  promo_code,
  user_membership_status
) => {
  window.dataLayer?.push({
    event: 'premium_checkout_clicked',
    sub_plan: type,
    promo_code: promo_code || null,
    user_membership_status
  })
}

export const premium_canceled_event = (details, user) => {
  const event = {
    event: 'premium_canceled',
    ...camelToSnake(details),
    ...set_user_properties(user)
  }
  delete event.user_sub_plan
  window.dataLayer?.push(event)
}
export const sign_up_event = (tier = 'na', existingUser, user) => {
  window.dataLayer?.push({
    event: 'sign_up',
    method: 'email',
    user_alo_tier: tier,
    is_existing_awc_user: existingUser ? 'yes' : 'no',
    ...set_user_properties(user)
  })
}

export const sign_in_event = ({
  aa_linked_user = false,
  method = 'email',
  user = {}
}) => {
  window.dataLayer?.push({
    event: 'sign_in',
    location: 'sign_in',
    aa_linked_user: aa_linked_user ? 'yes' : 'no',
    ...set_user_properties(user),
    method
  })
}

export const verify_account_initiated_event = (location) =>
  storeEventInLocalStorage({
    event: 'account_linking_initiated',
    location
  })

export const verify_account_event = (
  is_existing_awc_user,
  user_alo_tier = 'na',
  user
) => {
  window.dataLayer?.push({
    event: 'account_linked',
    is_existing_awc_user: is_existing_awc_user ? 'yes' : 'no',
    user_alo_tier,
    ...set_user_properties(user)
  })
}

export const premium_unlock_click_event = (membership_location) => {
  storeEventInLocalStorage({
    event: 'premium_unlock_click',
    membership_location
  })
}

export const survey_started_event = () => {
  window.dataLayer?.push({
    event: 'new_survey_started'
  })
}

export const survey_skipped_event = () => {
  window.dataLayer?.push({
    event: 'new_survey_skipped'
  })
}

export const survey_finished_event = () => {
  window.dataLayer?.push({
    event: 'new_survey_finished'
  })
}

export const code_redeemed_event = (gtm_event_params, type) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'code_redeemed',
      type: type,
      promo_code: gtm_event_params.promo_code,
      unique_code: gtm_event_params.unique_code || ''
    })
  }
}

// method param accepts: [email, facebook, apple]
export const sign_up_initiated_event = (method, location = '') => {
  window.dataLayer?.push({
    event: 'sign_up_initiated',
    method: method,
    location
  })
}

const stripQueryParams = (...keys) => {
  const cleanUrl = new URL(window.location.href)
  keys.forEach((key) => cleanUrl.searchParams.delete(key))
  const cleaned =
    cleanUrl.pathname +
    (cleanUrl.searchParams.toString()
      ? `?${cleanUrl.searchParams.toString()}`
      : '') +
    cleanUrl.hash
  window.history.replaceState({}, '', cleaned)
}

// Fires sign_up_initiated when landing with ?sign_up_initiated=true&location=...
export const sign_up_initiated_from_query_params = (defaultLocation = '') => {
  const params = new URLSearchParams(window.location.search)
  if (params.get('sign_up_initiated')?.toString() !== 'true') return false

  const location = params.get('location')?.toString() || defaultLocation
  const method = params.get('method')?.toString() || 'email'
  sign_up_initiated_event(method, location)
  stripQueryParams('sign_up_initiated', 'location', 'method')

  return true
}

// Fires sign_up when landing with ?sign_up=true&location=... (e.g. Builder form successUrl).
// Mirrors the sign_up_initiated query-param pattern on /signup.
export const sign_up_from_query_params = (user = {}) => {
  const params = new URLSearchParams(window.location.search)
  if (params.get('sign_up')?.toString() !== 'true') return false

  const location = params.get('location')?.toString() || ''
  const method = params.get('method')?.toString() || 'email'

  window.dataLayer?.push({
    event: 'sign_up',
    method,
    location,
    user_alo_tier: user?.userAloTier || user?.user_alo_tier || 'na',
    is_existing_awc_user: 'no',
    ...set_user_properties(user)
  })

  stripQueryParams('sign_up', 'location', 'method')

  return true
}

const format_filter_values = (type, value, options) => {
  let filterValue
  let filterType

  switch (type) {
    case 'instructors':
      filterValue = value.user?.name
      filterType = 'instructor_filtered'
      break
    case 'styles':
      // Temporal solution for GA4 phase one
      // The filter value selected by the user is supposed to be stored in value.slug.
      // However, due to an issue with iOS not sending the value correctly,
      // we are sending the primary category instead.
      filterValue =
        options.root && options.root.slug ? options.root.slug : value.slug
      filterType = 'style_filtered'
      break
    case 'intensities':
      filterValue = value.level
      filterType = 'intensity_filtered'
      break
    case 'difficulties':
      filterValue = value
      filterType = 'difficulty_filtered'
      break
    case 'duration':
      filterValue = `${value.min}-${value.max}`
      filterType = 'duration_filtered'
      break
    case 'focus_area':
      filterValue = value.level
      filterType = 'focus_area_filtered'
      break
    case 'equipment':
      filterValue = value.level
      filterType = 'equipment_filtered'
      break
    case 'yoga':
    case 'fitness':
    case 'mindfulness':
    case 'wellness':
    case 'skills':
      filterValue = value.level
      filterType = 'style_filtered'
      break
    case 'availability':
      filterValue = value.level
      filterType = 'availability_filtered'
      break
    default:
      filterValue = value
      filterType = type
      break
  }
  return {
    formattedValue: filterValue,
    formattedType: filterType
  }
}

export const class_series_finder_filter_event = (
  type,
  value,
  options = null
) => {
  const { formattedType } = format_filter_values(type, value, options)

  if (window.dataLayer) {
    window.dataLayer.push({
      event: formattedType,
      filter_value: value
    })
  }
}

export const sort_by_event = (location, value) => {
  let filterValue

  switch (value) {
    case 'az':
      filterValue = 'a-z'
      break
    case 'date':
      filterValue = 'newest'
      break
    case 'rank':
      filterValue = 'popular'
      break
    case 'relevance':
      filterValue = 'relevant'
      break
    default:
      filterValue = value
  }
  // This event has to trigger with just these two values
  // even if the component calling it is located in other parts of the site.
  if (window.dataLayer && (location === 'classes' || location === 'programs')) {
    window.dataLayer.push({
      event: 'sorted_by',
      location: location === 'classes' ? 'class_finder' : 'series_finder',
      filter_value: filterValue
    })
  }
}

export const welcome_event_viewed = () => {
  window.dataLayer?.push({
    event: 'Welcome_page_viewed'
  })
}

export const welcome_event_tapped = () => {
  window.dataLayer?.push({
    event: 'Welcome_page_tapped'
  })
}

export const home_signed_in_billboard_click_event = (title, position) => {
  window.dataLayer?.push({
    event: 'home_signed_in_billboard_click',
    campaign_name: title,
    position: position
  })
}

export const gift_card_added_event = () => {
  window.dataLayer.push({
    event: 'gift_card_added'
  })
}

export const gift_card_intent_event = () => {
  window.dataLayer.push({
    event: 'gift_card_intent'
  })
}

export const casting_initiated_event = (method) => {
  window.dataLayer.push({
    event: 'casting_initiated',
    method
  })
}

export const change_practice_tab_event = (tab) => {
  const eventMap = {
    series: 'saved_series_click',
    classes: 'saved_classes_click',
    completed: 'completed_classes_click',
    playlists: 'my_playlists_click'
  }
  window.dataLayer.push({
    event: eventMap[tab] || '',
    location: 'my_practice'
  })
}
export const class_series_comment_events = (
  contentType,
  eventType,
  seriesName,
  instructor,
  className,
  videoTitle
) => {
  const eventMap = {
    series: `series_comment_${eventType}`,
    class: `class_comment_${eventType}`
  }
  window.dataLayer.push({
    event: eventMap[contentType] || '',
    ...(videoTitle !== undefined && { title: videoTitle }),
    ...(className !== undefined && { class: className }),
    series: seriesName,
    instructor
  })
}

export const class_series_like_and_dislike_events = ({
  contentType,
  eventType,
  seriesName,
  instructor,
  className,
  videoTitle,
  style
}) => {
  const eventMap = {
    series: `series_thumbs_${eventType}`,
    class: `class_thumbs_${eventType}`
  }
  window.dataLayer.push({
    event: eventMap[contentType] || '',
    ...(videoTitle !== undefined && { title: videoTitle }),
    ...(style !== undefined && { style }),
    ...(className !== undefined && { class: className }),
    series: seriesName,
    instructor
  })
}

export const storeEventInLocalStorage = (eventData) => {
  let events = []
  try {
    const storage_data =
      JSON.parse(localStorage.getItem('ga4_event_list')) || []
    if (Array.isArray(storage_data)) {
      events = storage_data
    }
  } catch (e) {
    events = []
  }
  localStorage.setItem('ga4_event_list', JSON.stringify([...events, eventData]))
}

export const retrieveAndClearEvents = () => {
  let events = []
  try {
    events = JSON.parse(localStorage.getItem('ga4_event_list')) || []
  } catch (e) {
    events = []
  }
  if (events && Array.isArray(events) && events.length > 0) {
    window.dataLayer.push(...events)
    localStorage.removeItem('ga4_event_list')
  }
}

/**
 * Refer a friend — GTM dataLayer. Map in GTM to GA4 (e.g. custom event `refer_a_friend`).
 * @param {Object} params
 * @param {'open'|'close'|'share'} params.interaction
 * @param {string} [params.entry_point] footer | profile_menu | hamburger_menu | referral_page | unknown
 * @param {'modal'|'page'} [params.share_surface]
 * @param {'copy_link'|'email'|'facebook'|'message'|'more_share'|null} [params.destination] when interaction is share — same `destination` values as `class_social_share`
 */
export const refer_a_friend_event = ({
  interaction,
  entry_point = 'unknown',
  share_surface = 'page',
  destination = null
}) => {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  const payload = {
    event: 'refer_a_friend',
    interaction,
    entry_point,
    share_surface
  }
  if (destination != null) {
    payload.destination = destination
  }
  window.dataLayer.push(payload)
}

// --- Community Events (GTM dataLayer) -----------------------------------
// Client-side events for the Community (Luma) Events feature. Mirrors the
// class/program instrumentation: plain dataLayer pushes where `location` is
// the surface the action happened on (my_events | upcoming | past | related |
// listing | event_detail), matching the `location` dimension used elsewhere.
const platform = LINK_SOURCE === 'iosApp' ? 'iOS' : 'web'

const community_event_payload = (event = {}) => ({
  community_event: event.title,
  luma_event_id: event.id,
  platform
})

export const community_event_detail_view_event = (event = {}) => {
  window.dataLayer?.push({
    event: 'community_event_detail_view',
    ...community_event_payload(event),
    location_type: event.locationType || null,
    is_past: event.isPast ? 'yes' : 'no'
  })
}

export const community_event_card_click_event = ({
  event = {},
  location,
  position
} = {}) => {
  window.dataLayer?.push({
    event: 'community_event_card_click',
    ...community_event_payload(event),
    location,
    ...(position != null && { position })
  })
}

export const community_event_rsvp_click_event = ({
  event = {},
  location,
  registrationStatus,
  isFull
} = {}) => {
  window.dataLayer?.push({
    event: 'community_event_rsvp_click',
    ...community_event_payload(event),
    location,
    registration_status: registrationStatus || 'none',
    is_full: isFull ? 'yes' : 'no'
  })
}

export const community_event_sign_in_to_register_click_event = ({
  event = {},
  location
} = {}) => {
  window.dataLayer?.push({
    event: 'community_event_sign_in_to_register_click',
    ...community_event_payload(event),
    location
  })
}

export const community_event_rsvp_success_event = ({
  event = {},
  resultStatus
} = {}) => {
  window.dataLayer?.push({
    event: 'community_event_rsvp_success',
    ...community_event_payload(event),
    result_status: resultStatus || 'registered'
  })
}

export const community_event_cancel_click_event = ({
  event = {},
  location,
  registrationStatus
} = {}) => {
  window.dataLayer?.push({
    event: 'community_event_cancel_click',
    ...community_event_payload(event),
    location,
    registration_status: registrationStatus || null
  })
}

export const community_event_cancel_success_event = ({ event = {} } = {}) => {
  window.dataLayer?.push({
    event: 'community_event_cancel_success',
    ...community_event_payload(event)
  })
}

export const community_event_add_to_calendar_event = ({
  event = {},
  calendarType
} = {}) => {
  window.dataLayer?.push({
    event: 'community_event_add_to_calendar',
    ...community_event_payload(event),
    calendar_type: calendarType
  })
}

export const community_event_location_filtered_event = (filterValue) => {
  window.dataLayer?.push({
    event: 'community_event_location_filtered',
    filter_value: filterValue || 'all_locations',
    platform
  })
}

export const community_event_date_filtered_event = (filterValue) => {
  window.dataLayer?.push({
    event: 'community_event_date_filtered',
    filter_value: filterValue || null,
    platform
  })
}
